import axios from "axios";

/**
 * Service Pricing Import API client.
 *
 * Three stages mirroring the backend module: template -> preview -> commit.
 * CSV rides as a string on the JSON body (no multipart). See the backend
 * docs/service-pricing-import/SCOPE.md for the contract.
 */

export type RowOverride = "skip" | "update";

export type RowStatus =
  | "ready"
  | "will_create_combo"
  | "already_priced"
  | "combo_not_found"
  | "unresolved"
  | "invalid_price";

export type PrimaryAction =
  | "ADD_SERVICE_ROW"
  | "CREATE_COMBO_AND_ROW"
  | "UPDATE_ROW"
  | "SKIP"
  | "INVALID";

export interface ResolvedRow {
  row: number;
  status: RowStatus;
  primaryAction: PrimaryAction;
  attachServiceToDepot: boolean;
  attachServiceToTemplate: boolean;
  createService: boolean;
  serviceId: string | null;
  serviceName: string | null;
  depotId: string | null;
  productId: string | null;
  comboId: string | null;
  templateProductId: string | null;
  context: {
    providerName?: string;
    depotName?: string;
    serviceArea?: string;
    productName?: string;
    currentLowCost?: string;
    currentHighCost?: string;
    currentMarkup?: string;
  };
  price: {
    lowCost: number;
    highCost: number;
    markup: number;
    baseCost: number;
  } | null;
  message?: string;
}

export interface PreviewResult {
  targetServiceId: string | null;
  targetServiceName: string | null;
  rows: ResolvedRow[];
  summary: {
    total: number;
    ready: number;
    willCreateCombo: number;
    alreadyPriced: number;
    invalid: number;
  };
  servicesToCreate: string[];
}

export interface CommitRowResult {
  row: number;
  outcome:
    | "created_combo"
    | "added_row"
    | "updated_row"
    | "skipped"
    | "invalid"
    | "error";
  comboId?: string | null;
  message?: string;
}

export interface CommitResult {
  targetServiceId: string | null;
  createdServiceId?: string | null;
  results: CommitRowResult[];
  summary: {
    createdCombos: number;
    addedRows: number;
    updatedRows: number;
    skipped: number;
    failed: number;
  };
}

/** Download the CSV template (returns the raw CSV string + filename). */
export const getPricingImportTemplate = async (input: {
  referenceServiceId: string | null;
  targetServiceId?: string | null;
  targetServiceName?: string | null;
}): Promise<{ filename: string; csv: string; rowCount: number } | { error: string }> => {
  try {
    const res = await axios.post(`/service-pricing-import/template`, input);
    return res?.data ?? { error: "Template returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Template generation failed." }
      : { error: "Template generation failed." };
  }
};

/** Preview an uploaded CSV — resolve + classify, no writes. */
export const previewPricingImport = async (input: {
  csv: string;
  targetServiceId?: string | null;
  targetServiceName?: string | null;
  override?: RowOverride;
  rowOverrides?: Record<number, RowOverride>;
}): Promise<PreviewResult | { error: string }> => {
  try {
    const res = await axios.post(`/service-pricing-import/preview`, input);
    return res?.data ?? { error: "Preview returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Preview failed." }
      : { error: "Preview failed." };
  }
};

/** Commit the import. Requires confirmCreateService when creating a service. */
export const commitPricingImport = async (input: {
  csv: string;
  targetServiceId?: string | null;
  targetServiceName?: string | null;
  override?: RowOverride;
  rowOverrides?: Record<number, RowOverride>;
  confirmCreateService?: boolean;
}): Promise<CommitResult | { error: string }> => {
  try {
    const res = await axios.post(`/service-pricing-import/commit`, input);
    return res?.data ?? { error: "Commit returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Commit failed." }
      : { error: "Commit failed." };
  }
};
